import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  GraduationCap,
  Wallet,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  FileText,
  Compass,
  Stethoscope,
  Briefcase,
  Sparkles,
  Building2,
  Globe2,
  Trophy,
  ClipboardCheck,
} from 'lucide-react';
import InlineConsultationCTA from '@/components/common/InlineConsultationCTA';

const SEO_TITLE = 'German Student Visa Guide 2026: The Complete No-Fail Roadmap';
const SEO_DESCRIPTION =
  'German Student Visa 2026 for Indians: \u20AC11,904 blocked account, document checklist, interview tips & step-by-step process.';

// ─── Timeline ───────────────────────────────────────────────────────
const TIMELINE = [
  { window: '4–6 months before intake', action: 'Begin visa application; book VFS / Consular Services Portal appointment slot.' },
  { window: '3–4 months before', action: 'Open blocked account (Sperrkonto) and deposit \u20AC11,904.' },
  { window: '2–3 months before', action: 'Secure health insurance (GKV/PKV) and gather all documents.' },
  { window: '1–2 months before', action: 'Submit visa application and attend interview.' },
  { window: '6–12 weeks after', action: 'Visa processing window (India 2026 baseline).' },
];

// ─── Blocked Account Providers ──────────────────────────────────────
const BLOCKED_PROVIDERS = [
  { name: 'Fintiba',  setup: '\u20AC159', monthly: '\u20AC9.90/month', firstYear: '\u20AC277.80', currentAcc: 'No',           bundle: 'Fintiba Plus', verdict: 'Most embassy recognition' },
  { name: 'Expatrio', setup: '\u20AC89',  monthly: '\u20AC5/month',     firstYear: '\u20AC149',    currentAcc: 'Yes (free)',   bundle: 'Value Package', verdict: 'Best with Girokonto' },
  { name: 'Coracle',  setup: '\u20AC99',  monthly: '\u20AC0/month',     firstYear: '\u20AC99',     currentAcc: 'No',           bundle: 'Coracle Prime', verdict: 'Lowest total cost' },
];

// ─── GKV vs PKV ─────────────────────────────────────────────────────
const INSURANCE_ROWS = [
  { criteria: 'Students under 30',     gkv: 'Mandatory eligibility (TK / AOK / Barmer)', pkv: 'Generally not an option without GKV exemption' },
  { criteria: 'Students over 30',      gkv: 'Usually not eligible for student rate',     pkv: 'Standard route' },
  { criteria: 'Cost (student rate)',   gkv: '\u2248 \u20AC125–130/month',                 pkv: 'Varies by provider & coverage' },
  { criteria: 'Accepted for visa',     gkv: 'Yes',                                        pkv: 'Yes (if minimum coverage met)' },
  { criteria: 'Accepted for enrollment', gkv: 'Yes',                                      pkv: 'Yes (with GKV exemption)' },
];

// ─── Document checklists ────────────────────────────────────────────
const DOCS_IDENTITY = [
  'Valid passport — issued in the last 10 years, ≥2 empty pages, valid for entire stay',
  '2 biometric photos (≤6 months old)',
  'VIDEX online application — printed twice, signed',
  'Declaration for contact info — signed twice',
  '2 A4 copies of passport data page',
];
const DOCS_ACADEMIC = [
  'Unconditional admission letter from a state-recognised German university',
  'Academic transcripts & past records',
  'Language certificate — TestDaF / DSH / Goethe (B2) OR IELTS / TOEFL',
  'APS certificate (Indian applicants — waived for IB / PhD / EU scholarships)',
];
const DOCS_FINANCIAL = [
  'Blocked account (Sperrkonto) confirmation — \u20AC11,904 deposited',
  'OR official scholarship letter (\u2265 \u20AC992/month)',
  'OR Verpflichtungserklärung from a German sponsor',
];
const DOCS_HEALTH = [
  'Health insurance certificate — required for visa AND university enrollment',
  'GKV (TK / AOK / Barmer) for under-30s; PKV for over-30s',
];

// ─── 7-step application ─────────────────────────────────────────────
const APPLICATION_STEPS = [
  { title: 'Open Blocked Account & Deposit Funds', desc: 'Choose Fintiba, Expatrio or Coracle — deposit \u20AC11,904 and obtain confirmation.' },
  { title: 'Secure Health Insurance', desc: 'Purchase German student health cover (GKV under 30, PKV over 30). Obtain certificate for visa.' },
  { title: 'Compile All Documents', desc: 'Identity, academic, financial, health — organise originals + copies in physical folders.' },
  { title: 'Book Appointment', desc: 'VFS Global OR new digital Consular Services Portal (live at all 167 visa sections worldwide).' },
  { title: 'Upload via Consular Services Portal', desc: 'Pre-upload documents digitally before in-person appointment to cut wait times.' },
  { title: 'Attend Interview & Submit Biometrics', desc: '10–15 min interview in English or German. Pay \u20AC75 visa fee.' },
  { title: 'Wait 6–12 Weeks for Decision', desc: 'No fast-track or expedited processing. Plan your travel dates accordingly.' },
];

// ─── Interview questions ────────────────────────────────────────────
const INTERVIEW_QS = [
  { q: 'Why Germany?', a: 'University ranking, programme uniqueness, research facilities — be specific. Avoid generic praise.' },
  { q: 'Why this course?', a: 'Direct connection to past education and future career goals.' },
  { q: 'How will you support yourself?', a: 'Mention blocked account (\u20AC11,904), monthly cap (\u20AC992), and 140-day work allowance.' },
  { q: 'What will you do after graduation?', a: 'Clear plan — return home or use the 18-month job-seeker visa for skilled work.' },
  { q: 'Do you intend to return home?', a: 'Acknowledge work-permit pathways but emphasise long-term plans aligned with home market or skilled migration.' },
];

// ─── Post-arrival ───────────────────────────────────────────────────
const POST_ARRIVAL = [
  { step: 'Secure accommodation + Wohnungsgeberbestätigung', when: 'Within 2 weeks' },
  { step: 'City Registration (Anmeldung) at Bürgeramt', when: '1–2 weeks of arrival' },
  { step: 'Apply for Residence Permit at Ausländerbehörde', when: 'Within 3 months' },
  { step: 'Activate blocked account, set €992 monthly transfers', when: 'Week 1' },
  { step: 'Get Tax ID (Steuer-ID) & open Girokonto', when: 'Within 4 weeks' },
];

// ─── 2026 updates ───────────────────────────────────────────────────
const KEY_UPDATES = [
  { title: 'Blocked account raised to €11,904', desc: 'Old €11,208 figure obsolete — use €992 × 12 for 2026 applications.' },
  { title: 'Remonstration appeal abolished', desc: 'No internal informal appeal after 1 July 2025 — the first application must be perfect.' },
  { title: 'Consular Services Portal live worldwide', desc: 'Digital pre-upload available at all 167 visa sections globally.' },
  { title: '18-month job-seeker visa', desc: 'Officially codified post-graduation work-search window.' },
  { title: 'Permanent residence in 2 years', desc: 'Down from 4+ years — for skilled work post-graduation.' },
  { title: 'Easier family reunification', desc: 'Reduced income & accommodation hurdles for spouse/children.' },
];

// ─── Anchor chips ───────────────────────────────────────────────────
const ANCHOR_LINKS = [
  { label: 'Germany APS Certificate Guide 2026', path: '/study-abroad/germany/aps-certificate/' },
  { label: 'Cost of Studying in Germany 2026', path: '/study-abroad/germany/cost-of-study/' },
  { label: 'Best Universities in Germany 2026', path: '/study-abroad/germany/best-universities/' },
  { label: 'Germany Chancenkarte Calculator', path: '/germany-chancenkarte-calculator' },
  { label: 'Education Loan Assistance', path: '/study-abroad/education-loan/' },
  { label: 'Free Profile Evaluation', path: '/study-abroad/free-profile-evaluation/?source=study-abroad-germany-student-visa-anchor' },
  { label: 'Book a Free Counselling Session', path: '/study-abroad/book-counselling/?source=study-abroad-germany-student-visa-anchor' },
];

// ─── FAQs ───────────────────────────────────────────────────────────
const FAQS = [
  { q: 'How much do I need to deposit in a German blocked account in 2026?', a: '€11,904 (€992/month × 12). This is the official 2026 figure — the old €11,208 amount is obsolete.' },
  { q: 'Which blocked account provider should I choose?', a: 'Coracle for lowest total cost (€0/month), Expatrio if you want a free Girokonto, Fintiba for maximum embassy recognition. All three are accepted at every German consulate.' },
  { q: 'Is the remonstration appeal still available in 2026?', a: 'No. The informal remonstration appeal was abolished on 1 July 2025. If your application is rejected, you must file a fresh application or pursue formal court challenges. The first application must be perfect.' },
  { q: 'Can I work while studying on a German Student Visa?', a: 'Yes — up to 140 full days or 280 half-days per year, capped at 20 hours/week during semester. Effective since the March 2024 Skilled Immigration Act update.' },
  { q: 'Do I need German language proficiency for an English-taught programme?', a: 'No, but a B1 level helps in the visa interview and daily life. For German-taught programmes you need TestDaF, DSH, or Goethe B2/C1.' },
  { q: 'How long does the German Student Visa take to process in 2026?', a: '6–12 weeks for India in 2026, 4–12 weeks globally. There is no fast-track — neither the embassy nor VFS can expedite the process.' },
  { q: 'What is the difference between the visa and the residence permit?', a: 'The National Visa (Type D) gets you INTO Germany (3–6 months validity). The Residence Permit (Aufenthaltstitel) lets you STAY for 1–2 years and must be applied for at the Ausländerbehörde within 3 months of arrival.' },
  { q: 'Is the Opportunity Card (Chancenkarte) a replacement for the Student Visa?', a: 'No. The Chancenkarte is a points-based job-seeker visa for those who already hold a recognised degree. If you already have a German university admission letter, you must apply for the Student Visa (Visum zu Studienzwecken).' },
];

// ─── Subcomponents ──────────────────────────────────────────────────
const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="max-w-3xl mb-8 lg:mb-12">
    {eyebrow && (
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/12 border border-[#C5A028]/35 text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
        {eyebrow}
      </div>
    )}
    <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold font-syne text-[#003087] leading-tight mb-3">
      {title}
    </h2>
    {subtitle && <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{subtitle}</p>}
  </div>
);

const BulletDot = ({ tone = 'gold' }) => {
  const m = { gold: 'bg-[#C5A028]', blue: 'bg-[#003087]', emerald: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500' };
  return <span className={`mt-1.5 inline-flex shrink-0 w-2 h-2 rounded-full ${m[tone] || m.gold}`} />;
};

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-2xl transition-all ${open ? 'border-[#C5A028]/50 bg-white shadow-md' : 'border-slate-200 bg-white'}`} data-testid={`de-visa-faq-item-${index}`}>
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5" data-testid={`de-visa-faq-toggle-${index}`}>
        <span className="text-sm sm:text-base font-semibold text-slate-900 leading-snug">{q}</span>
        <ChevronDown className={`shrink-0 w-5 h-5 mt-0.5 text-[#C5A028] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-sm sm:text-[15px] text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
};

const CtaInline = ({ tag, heading, copy, button, href, testid }) => (
  <div className="my-10 lg:my-14 rounded-3xl p-6 sm:p-9 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }} data-testid={testid}>
    <div aria-hidden="true" className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#C5A028]/14 blur-3xl" />
    <div className="relative grid lg:grid-cols-[1fr_auto] gap-6 items-center">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/35 text-[10.5px] font-bold tracking-[0.2em] text-[#FACC15] uppercase mb-3">
          {tag}
        </div>
        <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold font-syne leading-tight mb-2">{heading}</h3>
        <p className="text-sm sm:text-[15px] text-slate-300 leading-relaxed max-w-xl">{copy}</p>
      </div>
      <Link to={href} className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm sm:text-base px-6 py-3.5 rounded-full transition-all hover:-translate-y-0.5 whitespace-nowrap">
        {button}
        <ArrowUpRight className="w-4 h-4" />
      </Link>
    </div>
  </div>
);

// ─── Main page ──────────────────────────────────────────────────────
const GermanyStudentVisaPage = () => {
  useEffect(() => {
    document.title = SEO_TITLE;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    const prevDesc = metaDesc.getAttribute('content');
    metaDesc.setAttribute('content', SEO_DESCRIPTION);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const scriptEl = document.createElement('script');
    scriptEl.type = 'application/ld+json';
    scriptEl.setAttribute('data-de-visa-faq-schema', '1');
    scriptEl.text = JSON.stringify(schema);
    document.head.appendChild(scriptEl);

    // BreadcrumbList schema for SEO (Home → Study Abroad → Germany → Student Visa)
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Study Abroad', item: 'https://zafco.in/study-abroad/' },
        { '@type': 'ListItem', position: 3, name: 'Germany', item: 'https://zafco.in/study-abroad/germany/' },
        { '@type': 'ListItem', position: 4, name: 'Student Visa Guide' },
      ],
    };
    const breadcrumbEl = document.createElement('script');
    breadcrumbEl.type = 'application/ld+json';
    breadcrumbEl.setAttribute('data-de-visa-breadcrumb-schema', '1');
    breadcrumbEl.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbEl);

    return () => {
      if (prevDesc !== null) metaDesc.setAttribute('content', prevDesc);
      const existing = document.head.querySelector('script[data-de-visa-faq-schema]');
      if (existing) existing.remove();
      const existingBc = document.head.querySelector('script[data-de-visa-breadcrumb-schema]');
      if (existingBc) existingBc.remove();
    };
    // All referenced names (SEO_TITLE, SEO_DESCRIPTION, FAQS) are module-level
    // constants and therefore stable — this effect must run exactly once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-white zaf-prose" data-testid="germany-student-visa-page">
      {/* ============================== HERO ============================== */}
      <section className="relative overflow-hidden text-white" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 55%, #020617 100%)' }}>
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#C5A028]/14 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-24">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">
                <Globe2 className="w-3.5 h-3.5" />
                Germany Visa Guide · 2026
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-[10.5px] font-bold tracking-[0.22em] text-emerald-200 uppercase" data-testid="de-visa-updated-pill">
                <Calendar className="w-3.5 h-3.5" />
                Last Updated: 22 May 2026
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-[52px] font-bold font-syne leading-[1.05] tracking-tight mb-5" data-testid="de-visa-h1">
              German Student Visa Guide <span className="text-[#FACC15]">2026</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl mb-6">
              The complete no-fail roadmap for international students who already hold a German university admission letter.
              Updated for 2026 — including the new <strong>€11,904 blocked account</strong>, the abolished remonstration
              appeal, and the digital Consular Services Portal now live at all 167 visa sections worldwide.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/study-abroad/book-counselling/?source=study-abroad-germany-student-visa" className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5" data-testid="de-visa-hero-cta">
                Book Free Visa Counselling
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/study-abroad/free-profile-evaluation/?source=study-abroad-germany-student-visa-hero&country=germany" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all">
                Evaluate My Profile
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== FINANCIAL SNAPSHOT ============================== */}
      <section className="py-12 lg:py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="zaf-financial-snapshot rounded-3xl p-7 sm:p-12 grid lg:grid-cols-[auto_1fr] gap-8 items-center" data-testid="de-visa-financial-snapshot">
            <div className="text-center lg:text-left">
              <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-amber-800 mb-2">2026 Blocked Account Requirement</div>
              <div className="zaf-financial-snapshot-amount text-5xl sm:text-6xl lg:text-7xl mb-1">€11,904</div>
              <div className="text-sm font-semibold text-amber-900">€992 / month × 12</div>
            </div>
            <div className="space-y-2.5 text-sm sm:text-[15px] text-amber-900/90">
              <p className="leading-relaxed">
                <strong className="text-amber-900">The single most decisive number in your application.</strong> The previous
                €11,208 figure is obsolete — German consulates worldwide now require €11,904 in a Sperrkonto for 2026 intakes.
              </p>
              <p className="leading-relaxed">
                Once in Germany, you may withdraw a maximum of <strong>€992 per month</strong> from this account to support
                your living costs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== TIMELINE ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="When to Start" title="Eligibility & Timeline Matrix" subtitle="Backwards-plan from your intake date. Booking appointments takes time — start at month 6, not month 2." />
          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto" data-testid="de-visa-timeline-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100/70 text-[10.5px] tracking-[0.18em] uppercase text-slate-600">
                    <th className="px-5 sm:px-7 py-4 text-left font-bold">Timeline</th>
                    <th className="px-5 sm:px-7 py-4 text-left font-bold">Action Item</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {TIMELINE.map(t => (
                    <tr key={t.window} className="hover:bg-slate-50">
                      <td className="px-5 sm:px-7 py-4 font-bold text-slate-900 whitespace-nowrap">{t.window}</td>
                      <td className="px-5 sm:px-7 py-4 text-slate-700">{t.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== BLOCKED ACCOUNT PROVIDERS ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Sperrkonto Deep-Dive" title="Blocked Account Provider Comparison 2026" subtitle="All three are accepted at every German consulate. Choose by total first-year cost, current-account inclusion, or embassy recognition." />
          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto" data-testid="de-visa-providers-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100/70 text-[10.5px] tracking-[0.18em] uppercase text-slate-600">
                    <th className="px-4 sm:px-6 py-4 text-left font-bold">Provider</th>
                    <th className="px-4 sm:px-6 py-4 text-left font-bold">Setup</th>
                    <th className="px-4 sm:px-6 py-4 text-left font-bold">Monthly</th>
                    <th className="px-4 sm:px-6 py-4 text-left font-bold">Year 1 Total</th>
                    <th className="px-4 sm:px-6 py-4 text-left font-bold hidden md:table-cell">Free Current A/c</th>
                    <th className="px-4 sm:px-6 py-4 text-left font-bold hidden lg:table-cell">Verdict</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {BLOCKED_PROVIDERS.map(p => (
                    <tr key={p.name} className="hover:bg-slate-50">
                      <td className="px-4 sm:px-6 py-5 font-bold text-slate-900">{p.name}</td>
                      <td className="px-4 sm:px-6 py-5 text-slate-700">{p.setup}</td>
                      <td className="px-4 sm:px-6 py-5 text-slate-700">{p.monthly}</td>
                      <td className="px-4 sm:px-6 py-5 font-bold text-[#003087]">{p.firstYear}</td>
                      <td className="px-4 sm:px-6 py-5 text-slate-700 hidden md:table-cell">{p.currentAcc}</td>
                      <td className="px-4 sm:px-6 py-5 text-slate-700 hidden lg:table-cell">{p.verdict}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <CtaInline
            tag="Financial Setup"
            heading="Confused About the €11,904 Deposit?"
            copy="We help ZAFCO students set up their Sperrkonto and Health Insurance in under 24 hours with zero embassy friction."
            button="Get Financial Setup Support"
            href="/study-abroad/book-counselling/?source=study-abroad-germany-student-visa-cta-financial"
            testid="de-visa-cta-financial"
          />
        </div>
      </section>

      {/* ============================== POLICY ALERTS ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Critical 2026 Policy Changes" title="What Has Changed Since 2024" subtitle="Two policy shifts dramatically affect your application strategy. Both are non-negotiable for 2026 intakes." />
          <div className="grid lg:grid-cols-2 gap-5 lg:gap-7">
            <div className="zaf-alert-policy rounded-2xl p-6 sm:p-7" data-testid="de-visa-alert-remonstration">
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0" />
                <h3 className="text-base sm:text-lg font-bold font-syne text-amber-900">Remonstration Appeal Abolished</h3>
              </div>
              <p className="text-sm text-amber-900/90 leading-relaxed mb-2">
                Effective <strong>1 July 2025</strong>, the informal remonstration appeal route for visa rejections has been
                eliminated.
              </p>
              <p className="text-sm text-amber-900/90 leading-relaxed">
                <strong>Implication:</strong> You no longer get a second internal review. A rejection forces a fresh
                application or a formal court challenge. Make the first application airtight.
              </p>
            </div>

            <div className="zaf-alert-info rounded-2xl p-6 sm:p-7" data-testid="de-visa-alert-portal">
              <div className="flex items-center gap-3 mb-3">
                <Globe2 className="w-5 h-5 text-blue-700 shrink-0" />
                <h3 className="text-base sm:text-lg font-bold font-syne text-blue-900">Consular Services Portal Live Worldwide</h3>
              </div>
              <p className="text-sm text-blue-900/90 leading-relaxed mb-2">
                Launched <strong>1 January 2025</strong>, now active at all <strong>167 visa sections globally</strong>.
              </p>
              <p className="text-sm text-blue-900/90 leading-relaxed">
                <strong>Implication:</strong> Pre-upload your full document set digitally before your appointment to cut
                in-person time and eliminate postal delivery dependencies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== DOCUMENT CHECKLIST ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Zero-Error Edition" title="The Complete Document Checklist" subtitle="Four categories. Bring originals + 2 copies of each. Disorganised folders are an instant red flag." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5" data-testid="de-visa-checklist-grid">
            {[
              { tag: 'Identity', Icon: ShieldCheck, items: DOCS_IDENTITY,  tone: 'blue' },
              { tag: 'Academic', Icon: GraduationCap, items: DOCS_ACADEMIC, tone: 'gold' },
              { tag: 'Financial', Icon: Wallet, items: DOCS_FINANCIAL, tone: 'emerald' },
              { tag: 'Health', Icon: Stethoscope, items: DOCS_HEALTH, tone: 'amber' },
            ].map(({ tag, Icon, items, tone }) => (
              <div key={tag} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#C5A028]/40 transition-all" data-testid={`de-visa-checklist-${tag.toLowerCase()}`}>
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-[#003087]/8 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#003087]" />
                  </div>
                  <h3 className="text-sm font-bold tracking-[0.18em] uppercase text-[#003087]">{tag}</h3>
                </div>
                <ul className="space-y-2">
                  {items.map(it => (
                    <li key={it} className="flex items-start gap-2">
                      <BulletDot tone={tone} />
                      <span className="text-xs sm:text-[13px] text-slate-700 leading-relaxed">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <CtaInline
            tag="SOP Review"
            heading="Don't Leave Your Visa to Chance."
            copy="Your Motivationsschreiben is the deciding factor in 2026. Get a professional review from our German Visa Specialists to ensure your Return Intention and Career Plan meet embassy standards."
            button="Book a Document Review"
            href="/study-abroad/book-counselling/?source=study-abroad-germany-student-visa-cta-sop"
            testid="de-visa-cta-sop"
          />
        </div>
      </section>

      {/* ============================== APPLICATION STEPS ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="The Process" title="7-Step Application Flow" subtitle="Sequential. Skipping or reordering steps (e.g. booking biometrics before paying fees) commonly triggers system errors." />
          <div className="relative max-w-3xl mx-auto" data-testid="de-visa-steps-timeline">
            {APPLICATION_STEPS.map((step, idx) => (
              <div key={step.title} className="relative pl-16 sm:pl-20 pb-8 last:pb-0">
                {idx < APPLICATION_STEPS.length - 1 && (
                  <span className="absolute left-[19px] sm:left-[23px] top-12 bottom-0 w-px bg-gradient-to-b from-[#C5A028] via-[#C5A028]/40 to-transparent" />
                )}
                <span className="absolute left-0 top-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[#C5A028] to-[#EAB308] text-black font-bold flex items-center justify-center text-base sm:text-lg shadow-lg shadow-[#C5A028]/30 ring-4 ring-white">{idx + 1}</span>
                <div className="bg-slate-50 hover:bg-slate-100/70 transition-colors border border-slate-200 rounded-2xl p-5 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold font-syne text-[#003087] mb-1">{step.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== INTERVIEW Q&A ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="The Interview" title="5 Questions You Will Be Asked" subtitle="The interview lasts 10–15 minutes in English or German. Be concise, confident, and specific — vague answers raise red flags." />
          <div className="grid md:grid-cols-2 gap-4 lg:gap-5" data-testid="de-visa-interview-grid">
            {INTERVIEW_QS.map((qa, idx) => (
              <div key={qa.q} className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 hover:border-[#C5A028]/40 transition-all">
                <div className="flex items-start gap-3 mb-2.5">
                  <div className="w-8 h-8 shrink-0 rounded-full bg-[#003087]/10 flex items-center justify-center text-[#003087] font-bold text-sm">{idx + 1}</div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900 leading-snug pt-1">{qa.q}</h3>
                </div>
                <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed pl-11">{qa.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== HEALTH INSURANCE ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="GKV vs PKV" title="Health Insurance Compliance 2026" subtitle="Mandatory for both visa AND university enrollment. Choose by age — the rules cut at 30." />
          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto" data-testid="de-visa-insurance-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100/70 text-[10.5px] tracking-[0.18em] uppercase text-slate-600">
                    <th className="px-4 sm:px-6 py-4 text-left font-bold">Criteria</th>
                    <th className="px-4 sm:px-6 py-4 text-left font-bold">Statutory (GKV)</th>
                    <th className="px-4 sm:px-6 py-4 text-left font-bold">Private (PKV)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {INSURANCE_ROWS.map(r => (
                    <tr key={r.criteria} className="hover:bg-slate-50">
                      <td className="px-4 sm:px-6 py-4 font-bold text-slate-900">{r.criteria}</td>
                      <td className="px-4 sm:px-6 py-4 text-slate-700">{r.gkv}</td>
                      <td className="px-4 sm:px-6 py-4 text-slate-700">{r.pkv}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== POST-ARRIVAL ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="After You Land" title="Post-Arrival Checklist" subtitle="Visa = entry ticket. Residence Permit (Aufenthaltstitel) = legal right to stay. Apply within 3 months." />
          <div className="grid md:grid-cols-2 gap-4 lg:gap-5" data-testid="de-visa-post-arrival">
            {POST_ARRIVAL.map((p, idx) => (
              <div key={p.step} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-4">
                <div className="w-9 h-9 shrink-0 rounded-xl bg-[#003087] text-white font-bold flex items-center justify-center text-sm">{idx + 1}</div>
                <div className="flex-1">
                  <div className="text-sm sm:text-[15px] font-semibold text-slate-900 leading-snug mb-1">{p.step}</div>
                  <div className="inline-flex items-center gap-1.5 text-[11.5px] font-bold tracking-[0.16em] uppercase text-emerald-700">
                    <Calendar className="w-3 h-3" />{p.when}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== 2026 UPDATES ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="2026 Snapshot" title="Key Updates You Must Know" subtitle="Six policy shifts that materially change application strategy and post-graduation pathways." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-testid="de-visa-updates-grid">
            {KEY_UPDATES.map(u => (
              <div key={u.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-5 hover:border-[#C5A028]/40 transition-all">
                <Sparkles className="w-4 h-4 text-[#C5A028] mb-2" />
                <h3 className="text-sm sm:text-base font-bold font-syne text-[#003087] mb-2 leading-snug">{u.title}</h3>
                <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== ANCHOR CHIPS ============================== */}
      <section className="py-10 lg:py-14 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-slate-500 mb-3">Related Reading & Tools</div>
          <div className="flex flex-wrap gap-2.5" data-testid="de-visa-anchor-chips">
            {ANCHOR_LINKS.map(l => (
              <Link key={l.path} to={l.path} className="inline-flex items-center gap-1.5 bg-white hover:bg-[#003087] hover:text-white border border-slate-200 text-slate-700 text-xs sm:text-[13px] font-semibold px-4 py-2 rounded-full transition-colors">
                <Compass className="w-3 h-3" />
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== FAQ ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading eyebrow="Answers" title="Frequently Asked Questions" subtitle="Eight questions Indian families ask most often during the German Student Visa process." />
          <div className="space-y-3" data-testid="de-visa-faq-list">
            {FAQS.map((faq, idx) => <FaqItem key={faq.q} q={faq.q} a={faq.a} index={idx} />)}
          </div>
        </div>
      </section>

      {/* ============================== INLINE LEAD CAPTURE FORM (Zoho CRM) ============================== */}
      <InlineConsultationCTA
        eyebrow="Free Germany Visa Counselling"
        headline="Talk to a Germany Visa Expert — Free 1-on-1 Counselling"
        subheadline="Share your profile and a senior Germany counsellor will reach out within 60 seconds on WhatsApp. No spam, no obligation."
        bullets={[
          'Sperrkonto, APS & visa interview prep — covered in one session',
          'MEA-approved · 10,000+ students placed since 2015',
          'Free profile review before any commitment',
        ]}
        testId="de-visa-inline-consultation"
      />

      {/* ============================== RELATED GERMANY RESOURCES ============================== */}
      {/* Spoke→Spoke + Spoke→Hub linking — passes topical authority across the silo. */}
      <section className="py-14 lg:py-18 bg-white border-t border-slate-100" data-testid="germany-student-visa-related">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <div className="text-[10.5px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-2">
                Continue Your Germany Research
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold font-syne text-[#003087]">
                Related Germany Resources
              </h2>
            </div>
            <Link
              to="/study-abroad/germany/"
              className="text-sm font-semibold text-[#003087] hover:text-[#001F5C] inline-flex items-center gap-1.5"
              data-testid="back-to-germany-hub"
            >
              ← Back to Germany Hub
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { path: '/study-abroad/germany/aps-certificate/',       label: 'APS Certificate',       blurb: 'Mandatory academic verification. ₹18,500 fee · 4–8 wk timeline · 9-doc checklist.' },
              { path: '/study-abroad/germany/blocked-account/',       label: 'Blocked Account',       blurb: '€11,904 funding proof. Fintiba vs Expatrio vs Coracle compared.' },
              { path: '/study-abroad/germany/cost-of-study/',         label: 'Cost of Study',         blurb: 'Tuition + living + insurance. Total 1st-year budget ≈ ₹13L.' },
              { path: '/study-abroad/germany/scholarships/',          label: 'Scholarships',          blurb: 'DAAD · Erasmus+ · Deutschlandstipendium · Heinrich Böll & more.' },
              { path: '/study-abroad/germany/student-accommodation/', label: 'Student Accommodation', blurb: 'Studentenwerk · WG · private studios. Costs by city + 12-mo booking timeline.' },
              { path: '/study-abroad/germany/intakes/',               label: 'Intakes Calendar',      blurb: 'Winter (Oct) vs Summer (Apr) intakes — application deadlines & checklist.' },
            ].map((card) => (
              <Link
                key={card.path}
                to={card.path}
                className="group bg-slate-50 border border-slate-200 hover:border-[#003087] rounded-xl p-5 transition-all hover:shadow-md"
                data-testid={`de-visa-related-${card.path.split('/')[3]}`}
              >
                <div className="text-[10px] font-bold text-[#C5A028] uppercase tracking-wider mb-2">Germany Resource</div>
                <h3 className="text-base font-bold font-syne text-slate-900 group-hover:text-[#003087] mb-1.5 transition-colors">
                  {card.label}
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">{card.blurb}</p>
                <div className="mt-3 text-[#003087] text-xs font-semibold inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                  Read guide <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== FINAL LEAD CAPTURE ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl text-white" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }} data-testid="de-visa-lead-capture">
            <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#C5A028]/16 blur-3xl" />
            <div className="relative p-8 sm:p-12 lg:p-16 grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/18 border border-[#C5A028]/40 text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-4">
                  <GraduationCap className="w-3.5 h-3.5" />
                  ZAFCO Germany Concierge
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-[40px] font-bold font-syne leading-tight mb-4">Ready to Fly to Germany?</h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-6 max-w-xl">
                  From your VFS appointment to your first residence permit in Germany, ZAFCO provides a 360° concierge service.
                </p>
                <Link to="/study-abroad/book-counselling/?source=study-abroad-germany-student-visa" className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm sm:text-base px-7 py-4 rounded-full transition-all hover:-translate-y-0.5" data-testid="de-visa-lead-capture-cta">
                  Start My Visa Application Now
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="hidden lg:block">
                <ul className="space-y-3">
                  {[
                    { Icon: Wallet, label: 'Sperrkonto + €11,904 setup in under 24 hours' },
                    { Icon: FileText, label: 'Motivationsschreiben & SOP review by certified counsellors' },
                    { Icon: ClipboardCheck, label: 'VIDEX form + Consular Portal pre-upload assistance' },
                    { Icon: Building2, label: 'Anmeldung + Residence Permit hand-holding on arrival' },
                    { Icon: Briefcase, label: '18-month job-seeker visa & PR roadmap' },
                    { Icon: Trophy, label: 'Refusal-risk pre-check before the embassy submits' },
                  ].map(({ Icon, label }) => (
                    <li key={label} className="flex items-start gap-3 bg-white/[0.06] border border-white/10 rounded-xl px-4 py-3">
                      <Icon className="w-4 h-4 text-[#FACC15] mt-0.5 shrink-0" />
                      <span className="text-sm text-slate-200">{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GermanyStudentVisaPage;
